import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import NavBar from '../components/NavBar';
import axios from 'axios';

export default function AdminPanel() {
  const { API }                   = useAuth();
  const [users, setUsers]         = useState([]);
  const [pending, setPending]     = useState([]);
  const [sessions, setSessions]   = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [search, setSearch]       = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [message, setMessage]     = useState('');
  const [msgType, setMsgType]     = useState('success');

  const [form, setForm] = useState({
    fullName: '', companyName: '', email: '',
    phone: '', whatsAppNumber: '', password: '',
    isAdmin: false, ratePermissions: '',
    alertsAccess: false, billingAccess: false,
    subscriptionPlan: '', subscriptionStart: '',
    subscriptionExpiry: '',
  });

  const token   = localStorage.getItem('fx_token');
  const headers = { Authorization: 'Bearer ' + token };

  useEffect(() => {
    fetchUsers();
    fetchPending();
    fetchSessions();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(API + '/users', { headers });
      setUsers(res.data);
    } catch (e) { showMsg('Failed to load users', 'error'); }
  };

  const fetchPending = async () => {
    try {
      const res = await axios.get(API + '/users/pending', { headers });
      setPending(res.data);
    } catch (e) {}
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(API + '/users/sessions', { headers });
      setSessions(res.data);
    } catch (e) {}
  };

  const showMsg = (text, type = 'success') => {
    setMessage(text);
    setMsgType(type);
    setTimeout(() => setMessage(''), 4000);
  };

  const handleSubmit = async () => {
    try {
      if (isPending) {
        // APPROVE pending client
        await axios.post(API + '/users/approve/' + editUser.id, {
          subscriptionPlan:   form.subscriptionPlan,
          subscriptionStart:  form.subscriptionStart || null,
          subscriptionExpiry: form.subscriptionExpiry || null,
          alertsAccess:       form.alertsAccess,
          billingAccess:      form.billingAccess,
          ratePermissions:    form.ratePermissions,
        }, { headers });
        showMsg('Client approved successfully');
      } else if (editUser) {
        // EDIT existing client
        await axios.put(API + '/users/' + editUser.id, {
          fullName:           form.fullName,
          companyName:        form.companyName,
          phone:              form.phone,
          whatsAppNumber:     form.whatsAppNumber,
          isActive:           true,
          isApproved:         true,
          ratePermissions:    form.ratePermissions,
          alertsAccess:       form.alertsAccess,
          billingAccess:      form.billingAccess,
          subscriptionPlan:   form.subscriptionPlan,
          subscriptionStart:  form.subscriptionStart || null,
          subscriptionExpiry: form.subscriptionExpiry || null,
          newPassword:        form.password || undefined,
        }, { headers });
        showMsg('Client updated successfully');
      } else {
        // CREATE new client
        await axios.post(API + '/users', {
          fullName:           form.fullName,
          companyName:        form.companyName,
          email:              form.email,
          phone:              form.phone,
          whatsAppNumber:     form.whatsAppNumber,
          password:           form.password,
          isAdmin:            form.isAdmin,
          isActive:           true,
          isApproved:         true,
          ratePermissions:    form.ratePermissions,
          alertsAccess:       form.alertsAccess,
          billingAccess:      form.billingAccess,
          subscriptionPlan:   form.subscriptionPlan,
          subscriptionStart:  form.subscriptionStart || null,
          subscriptionExpiry: form.subscriptionExpiry || null,
        }, { headers });
        showMsg('Client created successfully');
      }
      setShowForm(false);
      setEditUser(null);
      setIsPending(false);
      resetForm();
      fetchUsers();
      fetchPending();
    } catch (e) {
      showMsg(e.response?.data || 'Error saving client', 'error');
    }
  };

  const handleApprove = (u) => {
    setEditUser(u);
    setIsPending(true);
    setForm({
      fullName:          u.fullName,
      companyName:       u.companyName || '',
      email:             u.email,
      phone:             u.phone || '',
      whatsAppNumber:    u.whatsAppNumber || '',
      password:          '',
      isAdmin:           false,
      ratePermissions:   '',
      alertsAccess:      false,
      billingAccess:     false,
      subscriptionPlan:  '',
      subscriptionStart: '',
      subscriptionExpiry: '',
    });
    setShowForm(true);
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setIsPending(false);
    setForm({
      fullName:          u.fullName,
      companyName:       u.companyName || '',
      email:             u.email,
      phone:             u.phone || '',
      whatsAppNumber:    u.whatsAppNumber || '',
      password:          '',
      isAdmin:           u.isAdmin,
      ratePermissions:   u.ratePermissions || '',
      alertsAccess:      u.alertsAccess || false,
      billingAccess:     u.billingAccess || false,
      subscriptionPlan:  u.subscriptionPlan || '',
      subscriptionStart: u.subscriptionStart
        ? u.subscriptionStart.split('T')[0] : '',
      subscriptionExpiry: u.subscriptionExpiry
        ? u.subscriptionExpiry.split('T')[0] : '',
    });
    setShowForm(true);
  };

  const handleResetPassword = async (u) => {
    const newPass = window.prompt(
      'Enter new password for ' + u.fullName + ':');
    if (!newPass) return;
    if (newPass.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(API + '/users/' + u.id, {
        fullName:           u.fullName,
        companyName:        u.companyName || '',
        phone:              u.phone || '',
        whatsAppNumber:     u.whatsAppNumber || '',
        isActive:           u.isActive,
        isApproved:         u.isApproved,
        ratePermissions:    u.ratePermissions || '',
        alertsAccess:       u.alertsAccess || false,
        billingAccess:      u.billingAccess || false,
        subscriptionPlan:   u.subscriptionPlan || '',
        subscriptionStart:  u.subscriptionStart || null,
        subscriptionExpiry: u.subscriptionExpiry || null,
        newPassword:        newPass,
      }, { headers });
      showMsg('Password reset for ' + u.fullName);
    } catch (e) {
      showMsg('Error resetting password', 'error');
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this client?')) return;
    try {
      await axios.delete(API + '/users/' + id, { headers });
      showMsg('Client deactivated');
      fetchUsers();
    } catch (e) {
      showMsg(e.response?.data || 'Error', 'error');
    }
  };

  const resetForm = () => setForm({
    fullName: '', companyName: '', email: '',
    phone: '', whatsAppNumber: '', password: '',
    isAdmin: false, ratePermissions: '',
    alertsAccess: false, billingAccess: false,
    subscriptionPlan: '', subscriptionStart: '',
    subscriptionExpiry: '',
  });

  const planColor = (plan) => {
    if (plan === 'Annual')      return '#22C55E';
    if (plan === 'Half Yearly') return '#3B82F6';
    if (plan === 'Quarterly')   return '#F59E0B';
    if (plan === 'Monthly')     return '#C9A84C';
    return '#8896A8';
  };

  const expiryColor = (expiry) => {
    if (!expiry) return '#8896A8';
    const days = Math.floor(
      (new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0)  return '#EF4444';
    if (days <= 7) return '#F59E0B';
    return '#22C55E';
  };

  const expiryLabel = (expiry) => {
    if (!expiry) return '--';
    const days = Math.floor(
      (new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0)  return 'EXPIRED';
    if (days === 0) return 'TODAY';
    if (days <= 7) return days + ' days left';
    return new Date(expiry).toLocaleDateString('en-IN');
  };

  const formTitle = () => {
    if (isPending)  return 'APPROVE CLIENT';
    if (editUser)   return 'EDIT CLIENT';
    return 'ADD NEW CLIENT';
  };

  const saveLabel = () => {
    if (isPending) return 'APPROVE CLIENT';
    if (editUser)  return 'SAVE CHANGES';
    return 'CREATE CLIENT';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080F1A' }}>
      <NavBar />
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>ADMIN PANEL</h1>
            <div style={styles.subtitle}>Client and session management</div>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditUser(null);
              setIsPending(false);
              resetForm();
            }}
            style={styles.addBtn}>
            + ADD CLIENT
          </button>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            ...styles.msgBox,
            background: msgType === 'success'
              ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
            border: msgType === 'success'
              ? '1px solid rgba(34,197,94,0.3)'
              : '1px solid rgba(239,68,68,0.3)',
            color: msgType === 'success' ? '#22C55E' : '#EF4444',
          }}>
            {message}
          </div>
        )}

        {/* Tabs */}
        <div style={styles.tabRow}>
          {[
            { key: 'users',    label: 'CLIENTS (' + users.length + ')' },
            { key: 'pending',  label: 'PENDING (' + pending.length + ')' },
            { key: 'sessions', label: 'SESSIONS (' + sessions.length + ')' },
          ].map(tab => (
            <button key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                ...styles.tab,
                background: activeTab === tab.key
                  ? 'rgba(201,168,76,0.1)' : 'transparent',
                color:  activeTab === tab.key ? '#C9A84C' : '#8896A8',
                border: activeTab === tab.key
                  ? '1px solid rgba(201,168,76,0.3)'
                  : '1px solid #1E3050',
              }}>
              {tab.label}
              {tab.key === 'pending' && pending.length > 0 && (
                <span style={styles.badge}>{pending.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Search Box */}
        {activeTab === 'users' && (
          <div style={{ marginBottom: '16px' }}>
            <input
              style={{
                background: '#111E2E', border: '1px solid #1E3050',
                borderRadius: '6px', padding: '9px 14px',
                color: '#E8EDF5', fontSize: '13px',
                fontFamily: 'Inter, sans-serif', outline: 'none',
                width: '300px',
              }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, company, email, phone..."
            />
          </div>
        )}

        {/* ── CLIENTS TAB ── */}
        {activeTab === 'users' && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['NAME / COMPANY', 'EMAIL', 'PHONE',
                    'SUBSCRIPTION', 'EXPIRY', 'ACCESS',
                    'STATUS', 'ACTIONS'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.filter(u =>
                  u.fullName?.toLowerCase().includes(search.toLowerCase()) ||
                  u.companyName?.toLowerCase().includes(search.toLowerCase()) ||
                  u.email?.toLowerCase().includes(search.toLowerCase()) ||
                  u.phone?.includes(search)
                ).map((u, i) => (
                  <tr key={u.id} style={{
                    background: i % 2 === 0 ? '#0D1B2A' : '#111E2E'
                  }}>
                    <td style={styles.tdBold}>
                      {u.fullName}
                      {u.isAdmin && (
                        <span style={styles.adminBadge}>ADMIN</span>
                      )}
                      {u.googleId && (
                        <span style={styles.googleBadge}>G</span>
                      )}
                      {u.companyName && (
                        <div style={styles.company}>{u.companyName}</div>
                      )}
                    </td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}>{u.phone || '--'}</td>
                    <td style={styles.td}>
                      {u.subscriptionPlan ? (
                        <span style={{
                          ...styles.planBadge,
                          color: planColor(u.subscriptionPlan),
                          borderColor: planColor(u.subscriptionPlan) + '44',
                        }}>
                          {u.subscriptionPlan.toUpperCase()}
                        </span>
                      ) : '--'}
                    </td>
                    <td style={{
                      ...styles.td,
                      color: expiryColor(u.subscriptionExpiry),
                      fontWeight: 600,
                    }}>
                      {expiryLabel(u.subscriptionExpiry)}
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <span style={{
                          ...styles.accessDot,
                          background: u.alertsAccess ? '#22C55E' : '#374151',
                        }}>A</span>
                        <span style={{
                          ...styles.accessDot,
                          background: u.billingAccess ? '#22C55E' : '#374151',
                        }}>B</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        background: u.isActive
                          ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                        color:  u.isActive ? '#22C55E' : '#EF4444',
                        border: u.isActive
                          ? '1px solid rgba(34,197,94,0.2)'
                          : '1px solid rgba(239,68,68,0.2)',
                      }}>
                        {u.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEdit(u)}
                          style={styles.editBtn}>EDIT</button>
                        {!u.isAdmin && !u.googleId && (
                          <button
                            onClick={() => handleResetPassword(u)}
                            style={styles.resetBtn}>PWD</button>
                        )}
                        {!u.isAdmin && u.isActive && (
                          <button
                            onClick={() => handleDeactivate(u.id)}
                            style={styles.deactivateBtn}>OFF</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── PENDING TAB ── */}
        {activeTab === 'pending' && (
          <div style={styles.tableWrap}>
            {pending.length === 0 ? (
              <div style={styles.empty}>No pending approvals</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {['NAME', 'COMPANY', 'EMAIL',
                      'PHONE', 'REGISTERED', 'ACTION'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pending.map((u, i) => (
                    <tr key={u.id} style={{
                      background: i % 2 === 0 ? '#0D1B2A' : '#111E2E'
                    }}>
                      <td style={styles.tdBold}>{u.fullName}</td>
                      <td style={styles.td}>{u.companyName || '--'}</td>
                      <td style={styles.td}>{u.email}</td>
                      <td style={styles.td}>{u.phone || '--'}</td>
                      <td style={{ ...styles.td, fontSize: '12px' }}>
                        {new Date(u.createdAt).toLocaleString('en-IN')}
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={() => handleApprove(u)}
                          style={styles.approveBtn}>
                          APPROVE
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === 'sessions' && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['NAME', 'EMAIL', 'LOGIN TIME', 'LAST ACTIVE'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={styles.empty}>
                      No active sessions
                    </td>
                  </tr>
                ) : sessions.map((s, i) => (
                  <tr key={s.id} style={{
                    background: i % 2 === 0 ? '#0D1B2A' : '#111E2E'
                  }}>
                    <td style={styles.tdBold}>{s.fullName}</td>
                    <td style={styles.td}>{s.email}</td>
                    <td style={styles.td}>
                      {new Date(s.loginAt).toLocaleString('en-IN')}
                    </td>
                    <td style={{ ...styles.td, color: '#22C55E' }}>
                      {new Date(s.lastActiveAt).toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '12px 16px' }}>
              <button onClick={fetchSessions} style={styles.refreshBtn}>
                REFRESH
              </button>
            </div>
          </div>
        )}

        {/* ── ADD / EDIT / APPROVE FORM ── */}
        {showForm && (
          <div style={styles.overlay}>
            <div style={styles.formCard}>
              <h2 style={styles.formTitle}>{formTitle()}</h2>

              {isPending && (
                <div style={styles.pendingInfo}>
                  <div style={styles.pendingRow}>
                    <span style={styles.pendingLabel}>Name</span>
                    <span style={styles.pendingValue}>{form.fullName}</span>
                  </div>
                  <div style={styles.pendingRow}>
                    <span style={styles.pendingLabel}>Email</span>
                    <span style={styles.pendingValue}>{form.email}</span>
                  </div>
                  {form.phone && (
                    <div style={styles.pendingRow}>
                      <span style={styles.pendingLabel}>Phone</span>
                      <span style={styles.pendingValue}>{form.phone}</span>
                    </div>
                  )}
                  {form.companyName && (
                    <div style={styles.pendingRow}>
                      <span style={styles.pendingLabel}>Company</span>
                      <span style={styles.pendingValue}>{form.companyName}</span>
                    </div>
                  )}
                </div>
              )}

              <div style={styles.formGrid}>

                {!isPending && (
                  <>
                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>FULL NAME</label>
                      <input style={styles.input}
                        value={form.fullName}
                        onChange={e => setForm({...form, fullName: e.target.value})}
                        placeholder="Client full name" />
                    </div>

                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>COMPANY NAME</label>
                      <input style={styles.input}
                        value={form.companyName}
                        onChange={e => setForm({...form, companyName: e.target.value})}
                        placeholder="Company name" />
                    </div>

                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>EMAIL</label>
                      <input style={{
                        ...styles.input, opacity: editUser ? 0.6 : 1 }}
                        value={form.email}
                        onChange={e => setForm({...form, email: e.target.value})}
                        placeholder="client@email.com"
                        disabled={!!editUser} />
                    </div>

                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>PHONE</label>
                      <input style={styles.input}
                        value={form.phone}
                        onChange={e => setForm({...form, phone: e.target.value})}
                        placeholder="Mobile number" />
                    </div>

                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>WHATSAPP NUMBER</label>
                      <input style={styles.input}
                        value={form.whatsAppNumber}
                        onChange={e => setForm({...form, whatsAppNumber: e.target.value})}
                        placeholder="WhatsApp number" />
                    </div>

                    <div style={styles.fieldWrap}>
                      <label style={styles.label}>
                        {editUser ? 'NEW PASSWORD (leave blank to keep)' : 'PASSWORD'}
                      </label>
                      <input type="password" style={styles.input}
                        value={form.password}
                        onChange={e => setForm({...form, password: e.target.value})}
                        placeholder={editUser ? 'Leave blank to keep' : 'Set password'} />
                    </div>
                  </>
                )}

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>SUBSCRIPTION PLAN</label>
                  <select style={styles.input}
                    value={form.subscriptionPlan}
                    onChange={e => setForm({...form, subscriptionPlan: e.target.value})}>
                    <option value="">-- Select Plan --</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Half Yearly">Half Yearly</option>
                    <option value="Annual">Annual</option>
                  </select>
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>SUBSCRIPTION START</label>
                  <input type="date" style={styles.input}
                    value={form.subscriptionStart}
                    onChange={e => setForm({...form, subscriptionStart: e.target.value})} />
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>SUBSCRIPTION EXPIRY</label>
                  <input type="date" style={styles.input}
                    value={form.subscriptionExpiry}
                    onChange={e => setForm({...form, subscriptionExpiry: e.target.value})} />
                </div>

                <div style={styles.fieldWrap}>
                  <label style={styles.label}>RATE PERMISSIONS (empty = all pairs)</label>
                  <input style={styles.input}
                    value={form.ratePermissions}
                    onChange={e => setForm({...form, ratePermissions: e.target.value})}
                    placeholder="e.g. USDINR,EURINR or leave empty" />
                </div>

                <div style={{ ...styles.fieldWrap, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>ACCESS CONTROLS</label>
                  <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
                    <label style={styles.toggleWrap}>
                      <input type="checkbox"
                        checked={form.alertsAccess}
                        onChange={e => setForm({...form, alertsAccess: e.target.checked})} />
                      <span style={styles.toggleLabel}>Alerts Access</span>
                    </label>
                    <label style={styles.toggleWrap}>
                      <input type="checkbox"
                        checked={form.billingAccess}
                        onChange={e => setForm({...form, billingAccess: e.target.checked})} />
                      <span style={styles.toggleLabel}>Billing Access</span>
                    </label>
                  </div>
                </div>

              </div>

              <div style={styles.formActions}>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditUser(null);
                    setIsPending(false);
                  }}
                  style={styles.cancelBtn}>CANCEL</button>
                <button
                  onClick={handleSubmit}
                  style={{
                    ...styles.saveBtn,
                    background: isPending
                      ? 'linear-gradient(135deg, #22C55E, #16A34A)'
                      : 'linear-gradient(135deg, #C9A84C, #8B6914)',
                  }}>
                  {saveLabel()}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '20px',
  },
  title: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '28px',
    fontWeight: 700, color: '#C9A84C', letterSpacing: '0.1em',
  },
  subtitle: { fontSize: '13px', color: '#8896A8', marginTop: '2px' },
  addBtn: {
    background: 'linear-gradient(135deg, #C9A84C, #8B6914)',
    border: 'none', borderRadius: '6px', padding: '10px 20px',
    color: '#080F1A', fontFamily: 'Rajdhani, sans-serif',
    fontSize: '14px', fontWeight: 700, letterSpacing: '0.08em',
    cursor: 'pointer',
  },
  msgBox: {
    borderRadius: '6px', padding: '10px 16px',
    marginBottom: '16px', fontSize: '13px',
  },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '16px' },
  tab: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '13px',
    fontWeight: 600, letterSpacing: '0.08em',
    padding: '6px 20px', borderRadius: '6px',
    cursor: 'pointer', position: 'relative',
  },
  badge: {
    position: 'absolute', top: '-6px', right: '-6px',
    background: '#EF4444', color: '#fff',
    borderRadius: '50%', width: '16px', height: '16px',
    fontSize: '10px', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  tableWrap: {
    background: '#0D1B2A', border: '1px solid #1E3050',
    borderRadius: '10px', overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '11px',
    fontWeight: 700, letterSpacing: '0.12em', color: '#C9A84C',
    padding: '12px 16px', textAlign: 'left',
    background: '#111E2E', borderBottom: '1px solid #1E3050',
    whiteSpace: 'nowrap',
  },
  td: {
    fontFamily: 'Inter, sans-serif', fontSize: '13px',
    color: '#E8EDF5', padding: '10px 16px',
    borderBottom: '1px solid rgba(30,48,80,0.5)',
    whiteSpace: 'nowrap',
  },
  tdBold: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '14px',
    fontWeight: 600, color: '#E8EDF5', padding: '10px 16px',
    borderBottom: '1px solid rgba(30,48,80,0.5)',
  },
  company: {
    fontSize: '11px', color: '#8896A8',
    fontFamily: 'Inter, sans-serif', fontWeight: 400, marginTop: '2px',
  },
  adminBadge: {
    marginLeft: '8px', fontSize: '10px', fontWeight: 700,
    background: 'rgba(201,168,76,0.15)', color: '#C9A84C',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '3px', padding: '1px 6px',
  },
  googleBadge: {
    marginLeft: '6px', fontSize: '10px', fontWeight: 700,
    background: 'rgba(66,133,244,0.15)', color: '#4285F4',
    border: '1px solid rgba(66,133,244,0.3)',
    borderRadius: '3px', padding: '1px 6px',
  },
  planBadge: {
    fontSize: '10px', fontFamily: 'Rajdhani, sans-serif',
    fontWeight: 700, letterSpacing: '0.08em',
    padding: '2px 8px', borderRadius: '4px', border: '1px solid',
  },
  accessDot: {
    width: '20px', height: '20px', borderRadius: '4px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '10px', fontWeight: 700, color: '#fff',
    fontFamily: 'Rajdhani, sans-serif',
  },
  statusBadge: {
    fontSize: '11px', fontFamily: 'Rajdhani, sans-serif',
    fontWeight: 600, letterSpacing: '0.08em',
    padding: '2px 8px', borderRadius: '4px',
  },
  editBtn: {
    background: 'rgba(37,99,168,0.15)',
    border: '1px solid rgba(37,99,168,0.3)',
    color: '#3B82C4', borderRadius: '4px', padding: '3px 10px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '11px',
    fontWeight: 600, cursor: 'pointer',
  },
  resetBtn: {
    background: 'rgba(234,179,8,0.1)',
    border: '1px solid rgba(234,179,8,0.3)',
    color: '#EAB308', borderRadius: '4px', padding: '3px 10px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '11px',
    fontWeight: 600, cursor: 'pointer',
  },
  deactivateBtn: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.2)',
    color: '#EF4444', borderRadius: '4px', padding: '3px 10px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '11px',
    fontWeight: 600, cursor: 'pointer',
  },
  approveBtn: {
    background: 'rgba(34,197,94,0.1)',
    border: '1px solid rgba(34,197,94,0.3)',
    color: '#22C55E', borderRadius: '4px', padding: '4px 14px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '12px',
    fontWeight: 700, cursor: 'pointer',
  },
  refreshBtn: {
    background: 'transparent', border: '1px solid #1E3050',
    color: '#8896A8', borderRadius: '4px', padding: '4px 14px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '12px',
    fontWeight: 600, cursor: 'pointer',
  },
  empty: {
    textAlign: 'center', padding: '32px',
    color: '#8896A8', fontFamily: 'Inter, sans-serif',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, overflowY: 'auto', padding: '20px',
  },
  formCard: {
    background: '#0D1B2A', border: '1px solid #1E3050',
    borderRadius: '12px', padding: '32px',
    width: '100%', maxWidth: '640px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
  },
  formTitle: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '20px',
    fontWeight: 700, color: '#C9A84C', letterSpacing: '0.1em',
    marginBottom: '24px',
  },
  pendingInfo: {
    background: '#111E2E', border: '1px solid #1E3050',
    borderRadius: '8px', padding: '16px', marginBottom: '24px',
  },
  pendingRow: {
    display: 'flex', gap: '12px', marginBottom: '8px',
  },
  pendingLabel: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '11px',
    fontWeight: 600, color: '#8896A8', letterSpacing: '0.1em',
    width: '80px', flexShrink: 0,
  },
  pendingValue: {
    fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#E8EDF5',
  },
  formGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '16px', marginBottom: '24px',
  },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    fontFamily: 'Rajdhani, sans-serif', fontSize: '10px',
    fontWeight: 600, color: '#8896A8', letterSpacing: '0.12em',
  },
  input: {
    background: '#111E2E', border: '1px solid #1E3050',
    borderRadius: '6px', padding: '9px 12px',
    color: '#E8EDF5', fontSize: '13px',
    fontFamily: 'Inter, sans-serif', outline: 'none',
  },
  toggleWrap: {
    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
  },
  toggleLabel: {
    fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#E8EDF5',
  },
  formActions: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
  },
  cancelBtn: {
    background: 'transparent', border: '1px solid #1E3050',
    color: '#8896A8', borderRadius: '6px', padding: '9px 20px',
    fontFamily: 'Rajdhani, sans-serif', fontSize: '13px',
    fontWeight: 600, cursor: 'pointer',
  },
  saveBtn: {
    border: 'none', borderRadius: '6px', padding: '9px 24px',
    color: '#080F1A', fontFamily: 'Rajdhani, sans-serif',
    fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  },
};